import React, { useState } from 'react';
import { X, Cpu, Loader2, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../lib/apiClient';

export default function AIAnalysisModal({ ea, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runAnalysis = async () => {
    setLoading(true);
    setError('');
    try {
      const accountParam = ea.account_id ? `?account_id=${ea.account_id}` : '';
      const res = await apiFetch(`/api/ai/analyze/${ea.magic}${accountParam}`, {
        method: 'POST',
        body: {},
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || `HTTP ${res.status}`);
      } else {
        setAnalysis(data);
      }
    } catch (e) {
      setError(e.message || 'Network error. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Parse markdown sections from analysis
  const renderAnalysis = (text) => {
    if (!text) return null;
    const sections = text.split(/### /);
    return sections.map((section, i) => {
      if (!section.trim()) return null;
      const lines = section.split('\n');
      const title = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();

      // Color-code the verdict
      let titleColor = 'text-gray-200';
      if (title === 'VERDICT') {
        if (content.includes('READY FOR LIVE')) titleColor = 'text-accent-green';
        else if (content.includes('CAUTIOUS')) titleColor = 'text-accent-orange';
        else if (content.includes('NEEDS MORE')) titleColor = 'text-accent-blue';
        else titleColor = 'text-accent-red';
      }

      return (
        <div key={i} className="mb-4">
          {title && <h4 className={`text-xs font-bold font-display uppercase tracking-wider mb-1.5 ${title === 'VERDICT' || title === 'SCORE' ? titleColor : 'text-accent-green'}`}>{title}</h4>}
          <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
            {content.split('\n').map((line, j) => {
              if (line.startsWith('- ')) {
                return <p key={j} className="ml-3 mb-1 text-gray-400">• {line.slice(2)}</p>;
              }
              if (line.startsWith('[') && title === 'VERDICT') {
                const verdict = line.replace(/[\[\]]/g, '');
                return <p key={j} className={`text-lg font-bold font-display ${titleColor}`}>{verdict}</p>;
              }
              if (line.startsWith('[') && title === 'SCORE') {
                const score = line.replace(/[\[\]]/g, '');
                return <p key={j} className="text-2xl font-bold font-mono text-accent-green">{score}</p>;
              }
              return line ? <p key={j} className="mb-1">{line}</p> : null;
            })}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent-purple/10 border border-accent-purple/20 flex items-center justify-center">
              <Cpu size={16} className="text-accent-purple" />
            </div>
            <div>
              <h3 className="text-sm font-semibold font-display text-gray-100">AI Analysis</h3>
              <p className="text-[11px] text-gray-500">{ea.ea_name || `EA_${ea.magic}`}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-dark-hover transition">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!analysis && !loading && !error && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-accent-purple/10 border border-accent-purple/20 flex items-center justify-center mx-auto mb-4">
                <Cpu size={28} className="text-accent-purple" />
              </div>
              <h4 className="text-gray-200 font-display font-semibold mb-2">Analyze {ea.ea_name || `EA_${ea.magic}`}</h4>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                AI will review all trade history, calculate risk metrics, and provide a detailed verdict on whether this EA is ready for live trading.
              </p>
              <button
                onClick={runAnalysis}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-blue text-white font-semibold rounded-xl hover:opacity-90 transition text-sm"
              >
                <Cpu size={16} /> Run AI Analysis
              </button>
              <p className="text-[10px] text-gray-600 mt-3">Uses your configured AI provider. API charges may apply.</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-16">
              <Loader2 size={32} className="text-accent-purple animate-spin mx-auto mb-4" />
              <p className="text-gray-400 text-sm font-display">Analyzing {ea.total_trades || 0} trades...</p>
              <p className="text-gray-600 text-xs mt-1">This may take 10-30 seconds</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-accent-red/10 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle size={24} className="text-accent-red" />
              </div>
              <p className="text-accent-red text-sm font-medium mb-2">Analysis Failed</p>
              <p className="text-gray-500 text-xs max-w-sm mx-auto mb-4">{error}</p>
              <button
                onClick={runAnalysis}
                className="px-4 py-2 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-300 hover:bg-dark-hover transition"
              >
                Try Again
              </button>
            </div>
          )}

          {analysis && (
            <div className="space-y-1">
              {/* Generated timestamp */}
              <p className="text-[10px] text-gray-600 mb-4">Generated: {new Date(analysis.generated_at).toLocaleString()}</p>
              {renderAnalysis(analysis.analysis)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
